import React, { useEffect, useState } from "react";
import { Moon, Sun, ChevronRight, ShieldCheck, Shield, Bell, BellRing, LogOut, Sparkles, Download, Pencil, MailCheck, UserPlus, Lightbulb, Gauge, DownloadCloud, Undo2, Trash2, LifeBuoy, BadgeCheck, Ban, Music, Volume2, VolumeX, Activity, Zap, Lock, Newspaper, Store, FileText, Image as ImageIcon, Video, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n, LANGS } from "@/context/I18nContext";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import SuggestionDialog from "@/components/chat/SuggestionDialog";
import InviteDialog from "@/components/chat/InviteDialog";
import HelpDialog from "@/components/chat/HelpDialog";
import VerifyEmailDialog from "@/components/chat/VerifyEmailDialog";
import BlockedContactsDialog from "@/components/chat/BlockedContactsDialog";
import DataUsageDialog from "@/components/chat/DataUsageDialog";
import AppLockDialog from "@/components/chat/AppLockDialog";
import SocialSettings from "@/components/chat/SocialSettings";
import MutedDialog from "@/components/chat/MutedDialog";
import { isStaff } from "@/lib/roles";
import TermsDialog from "@/components/chat/TermsDialog";
import RingtoneSettingsDialog from "@/components/chat/RingtoneSettingsDialog";
import { getUsage, fmtBytes } from "@/lib/dataMeter";
import { ultraLiteOn, setUltraLite, mediaTTLDays, setMediaTTLDays } from "@/lib/prefs";
import { getAutoDlTypes, setAutoDlType, clearDownloadedIndex } from "@/lib/mediaCache";
import { hasLock } from "@/lib/appLock";
import { RINGTONES, playTone, getSentTone, getRecvTone, getCallTone, soundsEnabled } from "@/lib/sound";
import { getInitials, avatarColor } from "@/lib/avatar";
import { enableWebPush, permissionState, showLocalTest } from "@/lib/push";
import { toast } from "sonner";

function Segmented({ value, options, onChange, testid }) {
  return (
    <div className="flex items-center gap-1 rounded-full bg-[var(--rc-bg)] border border-[var(--rc-border)] p-1" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${value === o.value ? "bg-[var(--rc-gold)] text-black" : "text-[var(--rc-muted)]"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`relative h-6 w-11 rounded-full transition-colors ${on ? "bg-[var(--rc-gold)]" : "bg-[var(--rc-surface-2)]"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

function ToneRow({ icon: Icon, label, value, onChange, disabled, testid }) {
  return (
    <div className={`flex items-center gap-3 py-3 ${disabled ? "opacity-50" : ""}`}>
      <span className="h-10 w-10 shrink-0 grid place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-gold)]"><Icon size={18} /></span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid}
        className="shrink-0 rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-1.5 text-sm text-[var(--rc-text)] focus:outline-none focus:border-[var(--rc-gold)]/50">
        {RINGTONES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
      </select>
    </div>
  );
}

export default function SettingsPage({ connected, onOpenProfile, onOpenAI, onOpenAdmin, onOpenMarket }) {
  const { user, setUser, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { lang, setLang } = useI18n();
  const fr = lang === "fr";
  const { t } = useI18n();
  const { canInstall, installed, isIOS, promptInstall } = usePWAInstall();
  const [notif, setNotif] = useState(null);
  const [receipts, setReceipts] = useState(user.read_receipts !== false);
  const [undoOn, setUndoOn] = useState(localStorage.getItem("rc_undo_send") !== "0");
  const [undoSecs, setUndoSecs] = useState(parseInt(localStorage.getItem("rc_undo_seconds") || "3", 10));
  const [dataSaver, setDataSaver] = useState(localStorage.getItem("rc_data_saver") !== "0");
  const [autoDlTypes, setAutoDlTypes] = useState(getAutoDlTypes());
  const [confirmClear, setConfirmClear] = useState(false);
  const [sounds, setSounds] = useState(soundsEnabled());
  const [toneSent, setToneSent] = useState(getSentTone());
  const [toneRecv, setToneRecv] = useState(getRecvTone());
  const [toneCall, setToneCall] = useState(getCallTone());
  const [sheet, setSheet] = useState(null); // suggestion | invite | help | verify | blocked | datausage | applock
  const [pushPerm, setPushPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [ultra, setUltra] = useState(ultraLiteOn());
  const [ttl, setTtl] = useState(mediaTTLDays());

  const setUltraMode = (v) => { setUltra(v); setUltraLite(v); };
  const setTtlDays = (v) => { setTtl(v); setMediaTTLDays(v); };

  const setUndo = (v) => { setUndoOn(v); localStorage.setItem("rc_undo_send", v ? "1" : "0"); };
  const setUndoS = (v) => { setUndoSecs(v); localStorage.setItem("rc_undo_seconds", String(v)); };
  const setSaver = (v) => { setDataSaver(v); localStorage.setItem("rc_data_saver", v ? "1" : "0"); };
  const toggleAutoDlType = (key) => { const next = setAutoDlType(key, !autoDlTypes[key]); setAutoDlTypes({ ...next }); };
  const setSoundsOn = (v) => { setSounds(v); localStorage.setItem("rc_sounds", v ? "1" : "0"); if (v) playTone("royal"); };
  const pickTone = (storeKey, setter) => (id) => { setter(id); localStorage.setItem(storeKey, id); playTone(id); };
  const clearStorage = async () => {
    try {
      if (typeof caches !== "undefined") { const keys = await caches.keys(); await Promise.all(keys.filter((k) => k.startsWith("rc-media")).map((k) => caches.delete(k))); }
      if (window.indexedDB?.databases) { const dbs = await indexedDB.databases(); dbs.forEach((d) => d.name && /rc[-_]?media|media/i.test(d.name) && indexedDB.deleteDatabase(d.name)); }
      clearDownloadedIndex();
      try { localStorage.removeItem("rc_media_index"); } catch {}
    } catch (e) {}
    setConfirmClear(false);
    toast.success(t("storage_cleared"));
  };

  useEffect(() => { api.get("/settings/notifications").then(({ data }) => setNotif(data)).catch(() => {}); }, []);
  const updateNotif = async (patch) => { setNotif((n) => ({ ...n, ...patch })); try { await api.put("/settings/notifications", patch); } catch (e) {} };
  const toggleReceipts = async () => {
    const v = !receipts;
    setReceipts(v);
    try { await api.put("/settings/privacy", { read_receipts: v }); setUser({ ...user, read_receipts: v }); }
    catch (e) { setReceipts(!v); }
  };

  const install = async () => {
    if (installed) return toast.success(t("set_installed_toast"));
    if (canInstall) return void promptInstall();
    toast(isIOS ? t("set_ios_install") : t("set_browser_install"), { duration: 6000 });
  };

  const avatar = user.avatar
    ? <img src={user.avatar} alt="" className="h-full w-full object-cover" />
    : <span className="text-2xl font-heading text-[var(--rc-gold)] tracking-wide">{getInitials(user.name)}</span>;

  const Row = ({ icon: Icon, title, subtitle, onClick, right, testid }) => (
    <div onClick={onClick} data-testid={testid} role="button" tabIndex={0} className="w-full flex items-center gap-3 py-3.5 cursor-pointer">
      <span className="h-10 w-10 shrink-0 grid place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-gold)]"><Icon size={19} /></span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block">{title}</span>
        {subtitle && <span className="block text-xs text-[var(--rc-muted)]">{subtitle}</span>}
      </span>
      {right !== undefined ? right : <ChevronRight size={18} className="text-[var(--rc-muted)]" />}
    </div>
  );

  return (
    <div className="h-full overflow-y-auto no-scrollbar bg-[var(--rc-bg)]" data-testid="settings-page">
      <div className="rc-safe-top px-5 pt-6 pb-2">
        <h1 className="font-heading text-5xl mb-5">{t("settings")}</h1>

        {/* Profile card */}
        <button onClick={onOpenProfile} data-testid="settings-profile-card"
          className="w-full flex items-center gap-4 p-4 rounded-3xl border border-[var(--rc-gold)]/30 bg-[var(--rc-surface)]">
          <div className="h-16 w-16 rounded-full grid place-items-center border-2 border-[var(--rc-gold)] overflow-hidden" style={{ backgroundColor: user.avatar ? "var(--rc-bg)" : avatarColor(user.name) }}>{avatar}</div>
          <div className="min-w-0 flex-1 text-left">
            <div className="text-xl font-medium truncate flex items-center gap-1.5">
              <span className="truncate">{user.name}</span>
              {user.email_verified && <BadgeCheck size={18} className="text-[var(--rc-gold)] shrink-0" data-testid="profile-verified-badge" />}
            </div>
            <div className="text-sm text-[var(--rc-muted)] truncate">{user.about || t("set_available")}</div>
            <div className="text-sm text-[var(--rc-gold)] truncate">{user.email}</div>
          </div>
          <Pencil size={18} className="text-[var(--rc-gold)]" />
        </button>

        {/* Admin quick-access — placed at top for easy locating */}
        {isStaff(user) && (
          <button onClick={onOpenAdmin} data-testid="settings-open-admin"
            className="w-full mt-3 flex items-center gap-3 p-4 rounded-2xl border border-[var(--rc-gold)]/40 bg-[var(--rc-gold)]/10">
            <span className="h-11 w-11 shrink-0 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black"><Shield size={20} /></span>
            <span className="min-w-0 flex-1 text-left">
              <span className="block font-medium text-[var(--rc-gold)]">{t("admin")}</span>
              <span className="block text-xs text-[var(--rc-muted)]">{t("admin_panel_desc")}</span>
            </span>
            <ChevronRight size={18} className="text-[var(--rc-gold)]" />
          </button>
        )}

        {/* Appearance */}
        <p className="mt-7 mb-3 text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{t("appearance").toUpperCase()}</p>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setTheme("dark")} data-testid="theme-dark-btn" className={`flex items-center justify-center gap-2 rounded-2xl py-4 border ${theme === "dark" ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}><Moon size={18} /> {t("dark")}</button>
          <button onClick={() => setTheme("light")} data-testid="theme-light-btn" className={`flex items-center justify-center gap-2 rounded-2xl py-4 border ${theme === "light" ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}><Sun size={18} /> {t("light")}</button>
        </div>

        {/* Language */}
        <p className="mt-7 mb-3 text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{t("language").toUpperCase()}</p>
        <div className="grid grid-cols-2 gap-3">
          {LANGS.map((L) => (
            <button key={L.code} onClick={() => setLang(L.code)} data-testid={`lang-${L.code}`} className={`flex items-center justify-center gap-2 rounded-2xl py-4 border ${lang === L.code ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>{L.flag} {L.label}</button>
          ))}
        </div>

        {/* network */}
        <div className="mt-4 flex items-center gap-2 rounded-2xl border border-[var(--rc-border)] px-4 py-3.5">
          <span className={`h-2.5 w-2.5 rounded-full ${connected ? "bg-emerald-400" : "bg-amber-400"}`} />
          <span>{t("network")}: {connected ? t("connected") : "…"}</span>
        </div>

        {/* Preferences */}
        <p className="mt-7 mb-1 text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{t("preferences").toUpperCase()}</p>
        <div className="divide-y divide-[var(--rc-border)]">
          <Row icon={Sparkles} title={t("ai_assistant")} subtitle={t("configure_ai")} onClick={onOpenAI} testid="settings-open-ai" />
          <Row icon={UserPlus} title={t("set_invite_title")} subtitle={t("set_invite_desc")} onClick={() => setSheet("invite")} testid="settings-invite" />
          <Row icon={Lightbulb} title={t("set_suggest_title")} subtitle={t("set_suggest_desc")} onClick={() => setSheet("suggestion")} testid="settings-suggestion" />
          {notif && (
            <Row icon={MailCheck} title={t("set_away_title")} subtitle={notif.email}
              right={<Toggle on={notif.away_email_enabled} onClick={() => updateNotif({ away_email_enabled: !notif.away_email_enabled })} testid="settings-away-toggle" />} />
          )}
          <Row icon={ShieldCheck} title={t("read_receipts")} subtitle={t("read_receipts_desc")}
            right={<Toggle on={receipts} onClick={toggleReceipts} testid="settings-receipts-toggle" />} />
          <Row icon={pushPerm === "granted" ? BellRing : Bell}
            title={t("notifications")}
            subtitle={
              pushPerm === "granted" ? (fr ? "Activées — appuyez sur « Tester »" : "On — tap Test to check")
              : pushPerm === "denied" ? (fr ? "Bloquées. iPhone : supprimez l'app de l'écran d'accueil et réinstallez, ou activez dans Réglages → RoyalChat Nova → Notifications" : "Blocked. iPhone: delete the Home Screen app & re-add, or enable in Settings → RoyalChat Nova → Notifications")
              : (fr ? "Appuyez pour activer les notifications" : "Tap to enable notifications")
            }
            onClick={pushPerm === "granted" ? undefined : async () => {
              try {
                const r = await enableWebPush();
                setPushPerm(permissionState());
                if (r.ok) toast.success(t("notifications_on") || "Notifications enabled");
                else if (r.reason === "denied") toast.error(t("notifications_blocked") || "Notifications blocked. On iPhone, delete & re-add the Home Screen app to be asked again.", { duration: 9000 });
                else if (r.reason === "ios-not-installed") toast(t("ios_install_for_push") || "On iPhone: tap Share → Add to Home Screen, then open RoyalChat Nova from your Home Screen to enable notifications.", { duration: 8000 });
                else if (r.reason === "native" || r.reason === "dismissed") { /* native handles it, or user closed the prompt — no error */ }
                else toast.error(t("set_not_supported"));
              } catch (e) { toast.error(t("set_notif_fail")); }
            }}
            right={pushPerm === "granted"
              ? <button data-testid="settings-notif-test" onClick={async (e) => { e.stopPropagation(); const r = await showLocalTest(); if (!r.ok) toast.error(fr ? "Échec du test" : "Test failed"); }}
                  className="text-xs font-semibold text-[var(--rc-gold)] rounded-full border border-[var(--rc-gold)]/40 px-3 py-1.5">{fr ? "Tester" : "Test"}</button>
              : <ChevronRight size={18} className="text-[var(--rc-muted)]" />}
            testid="settings-notif" />
          <Row icon={Download} title={installed ? t("set_installed") : t("install")} onClick={install} right={<ChevronRight size={18} className="text-[var(--rc-muted)]" />} testid="settings-install" />
        </div>

        {/* Social & Marketplace */}
        <p className="mt-7 mb-1 text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{t("set_social_market")}</p>
        <div className="divide-y divide-[var(--rc-border)]">
          <Row icon={Newspaper} title={t("set_social_title")} subtitle={t("set_social_desc")} onClick={() => setSheet("socialsettings")} testid="settings-social" />
          <Row icon={VolumeX} title={t("muted_accounts")} subtitle={t("set_muted_desc")} onClick={() => setSheet("muted")} testid="settings-muted" />
          {isStaff(user) && (
            <Row icon={Store} title={t("set_market_title")} subtitle={t("set_market_desc")} onClick={() => onOpenMarket && onOpenMarket()} testid="settings-market" />
          )}
          <Row icon={FileText} title={t("set_terms_title")} subtitle={t("set_terms_desc")} onClick={() => setSheet("terms")} testid="settings-terms" />
        </div>

        {/* Data & Storage */}
        <p className="mt-7 mb-1 text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{t("data_storage").toUpperCase()}</p>
        <div className="divide-y divide-[var(--rc-border)]">
          <Row icon={Zap} title={t("ultra_lite")} subtitle={t("ultra_lite_desc")}
            right={<Toggle on={ultra} onClick={() => setUltraMode(!ultra)} testid="settings-ultralite-toggle" />} />
          <Row icon={Gauge} title={t("data_saver")} subtitle={t("data_saver_desc")}
            right={<Toggle on={dataSaver} onClick={() => setSaver(!dataSaver)} testid="settings-datasaver-toggle" />} />
          <div className="py-3.5">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 shrink-0 grid place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-gold)]"><Undo2 size={19} /></span>
              <span className="min-w-0 flex-1"><span className="block">{t("undo_send_title")}</span><span className="block text-xs text-[var(--rc-muted)]">{t("undo_send_desc")}</span></span>
              <Toggle on={undoOn} onClick={() => setUndo(!undoOn)} testid="settings-undo-toggle" />
            </div>
            {undoOn && (
              <div className="mt-3 pl-13 flex justify-end">
                <Segmented value={undoSecs} onChange={setUndoS} testid="settings-undo-secs"
                  options={[{ value: 3, label: "3s" }, { value: 5, label: "5s" }, { value: 10, label: "10s" }]} />
              </div>
            )}
          </div>
          <div className="py-3.5">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 shrink-0 grid place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-gold)]"><DownloadCloud size={19} /></span>
              <span className="min-w-0 flex-1"><span className="block">{t("auto_download")}</span><span className="block text-xs text-[var(--rc-muted)]">{t("auto_download_desc")}</span></span>
            </div>
            <div className="mt-3 space-y-1">
              {[["image", t("mt_images"), ImageIcon], ["audio", t("mt_audio"), Music], ["video", t("mt_video"), Video], ["document", t("mt_documents"), FileText]].map(([key, label, Icon]) => (
                <div key={key} className="flex items-center gap-3 pl-13 py-1">
                  <Icon size={16} className="text-[var(--rc-muted)]" />
                  <span className="flex-1 text-sm">{label}</span>
                  <Toggle on={!!autoDlTypes[key]} onClick={() => toggleAutoDlType(key)} testid={`settings-autodl-${key}`} />
                </div>
              ))}
            </div>
          </div>
          <Row icon={Activity} title={t("data_usage")} subtitle={`${fmtBytes((getUsage().today.sent || 0) + (getUsage().today.recv || 0))} ${t("used_today")}`} onClick={() => setSheet("datausage")} right={<ChevronRight size={18} className="text-[var(--rc-muted)]" />} testid="settings-datausage" />
          <div className="py-3.5">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 shrink-0 grid place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-gold)]"><Trash2 size={19} /></span>
              <span className="min-w-0 flex-1"><span className="block">{t("auto_delete_media")}</span><span className="block text-xs text-[var(--rc-muted)]">{t("auto_delete_desc")}</span></span>
            </div>
            <div className="mt-3 flex justify-end">
              <Segmented value={ttl} onChange={setTtlDays} testid="settings-autodelete"
                options={[{ value: 0, label: t("autodl_off") }, { value: 7, label: "7j" }, { value: 30, label: "30j" }, { value: 90, label: "90j" }]} />
            </div>
          </div>
          <Row icon={Trash2} title={t("storage_cleanup")} subtitle={t("storage_cleanup_desc")} onClick={() => setConfirmClear(true)} testid="settings-clear-storage" />
        </div>

        {/* Sounds & ringtones */}
        <p className="mt-7 mb-1 text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{t("sounds_ringtones").toUpperCase()}</p>
        <div className="divide-y divide-[var(--rc-border)]">
          <Row icon={Volume2} title={t("sound_effects")} subtitle={t("sound_effects_desc")}
            right={<Toggle on={sounds} onClick={() => setSoundsOn(!sounds)} testid="settings-sounds-toggle" />} />
          <Row icon={Music} title={fr ? "Sonneries" : "Ringtones"} subtitle={fr ? "Choisir les sons d'appels et de messages" : "Choose call & message sounds"} onClick={() => setSheet("ringtones")} testid="settings-ringtones" />
        </div>

        {/* Privacy & Help */}
        <p className="mt-7 mb-1 text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{t("privacy_help").toUpperCase()}</p>
        <div className="divide-y divide-[var(--rc-border)]">
          <Row icon={BadgeCheck} title={t("verify_email")} subtitle={user.email_verified ? t("verified") : t("verify_email_desc")}
            onClick={() => setSheet("verify")}
            right={user.email_verified
              ? <span className="flex items-center gap-1 text-xs font-semibold text-[var(--rc-gold)]" data-testid="settings-verified-tag"><BadgeCheck size={15} /> {t("verified")}</span>
              : undefined}
            testid="settings-verify-email" />
          <Row icon={Lock} title={t("app_lock")} subtitle={hasLock() ? t("app_lock_on") : t("app_lock_desc")} onClick={() => setSheet("applock")} testid="settings-applock" />
          <Row icon={Ban} title={t("blocked_contacts")} subtitle={t("blocked_contacts_desc")} onClick={() => setSheet("blocked")} testid="settings-blocked" />
          <Row icon={LifeBuoy} title={t("help_support")} subtitle={t("help_support_desc")} onClick={() => setSheet("help")} testid="settings-help" />
        </div>

        <button onClick={logout} className="w-full mt-6 mb-8 flex items-center justify-center gap-2 rounded-2xl bg-red-500/10 text-red-400 border border-red-500/30 py-3.5 font-medium" data-testid="settings-logout">
          <LogOut size={18} /> {t("logout")}
        </button>
      </div>
      {sheet === "suggestion" && <SuggestionDialog onClose={() => setSheet(null)} />}
      {sheet === "invite" && <InviteDialog onClose={() => setSheet(null)} />}
      {sheet === "help" && <HelpDialog onClose={() => setSheet(null)} />}
      {sheet === "verify" && <VerifyEmailDialog onClose={() => setSheet(null)} />}
      {sheet === "blocked" && <BlockedContactsDialog onClose={() => setSheet(null)} />}
      {sheet === "datausage" && <DataUsageDialog onClose={() => setSheet(null)} />}
      {sheet === "applock" && <AppLockDialog onClose={() => setSheet(null)} />}
      {sheet === "socialsettings" && <SocialSettings onClose={() => setSheet(null)} />}
      {sheet === "muted" && <MutedDialog onClose={() => setSheet(null)} />}
      {sheet === "terms" && <TermsDialog onClose={() => setSheet(null)} />}
      {sheet === "ringtones" && <RingtoneSettingsDialog onClose={() => setSheet(null)} />}
      {confirmClear && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-5" onClick={() => setConfirmClear(false)} data-testid="clear-media-confirm">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-5 rc-pop">
            <div className="flex items-center gap-3 mb-2">
              <span className="h-10 w-10 grid place-items-center rounded-full bg-red-500/15 text-red-400 shrink-0"><AlertTriangle size={20} /></span>
              <div className="font-heading text-xl">{t("clear_media_title")}</div>
            </div>
            <p className="text-sm text-[var(--rc-muted)] mb-5">{t("clear_media_warn")}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmClear(false)} data-testid="clear-media-cancel"
                className="flex-1 rounded-full border border-[var(--rc-border)] py-2.5 font-medium text-[var(--rc-text)]">{t("cancel")}</button>
              <button onClick={clearStorage} data-testid="clear-media-confirm-btn"
                className="flex-1 rounded-full bg-red-500 text-white py-2.5 font-semibold">{t("clear_media_confirm")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
